import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

function getScroller() {
  const main = document.querySelector('main.main-content');
  if (main && main.scrollHeight > main.clientHeight) return main;
  return null;
}

/**
 * Saves the list scroll position per location (pathname + search) and
 * restores it once when the page is re-entered via a history POP (Back
 * button / hardware back). The app scrolls inside <main class="main-content">
 * (window stays fixed), so the container is the primary target with a
 * window fallback. `ready` should flip true once list content is rendered.
 */
export default function useScrollRestore(ready) {
  const location = useLocation();
  const navType = useNavigationType();
  const key = `scroll:${location.pathname}${location.search}`;
  const restoredRef = useRef(false);

  useEffect(() => {
    let raf = null;
    // Path this hook instance belongs to; used to ignore the clamp-to-0
    // scroll event the browser fires while the route is being swapped out.
    const ownPath = location.pathname;
    const currentPath = () => {
      const hash = window.location.hash || '';
      if (hash.startsWith('#/')) return hash.slice(1).split('?')[0];
      return window.location.pathname;
    };
    const save = (y) => {
      if (currentPath() !== ownPath) return;
      try {
        sessionStorage.setItem(key, String(Math.round(y)));
      } catch {
        /* storage unavailable — ignore */
      }
    };
    const onScroll = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const t = e.target === document ? null : e.target;
        save(t ? t.scrollTop : window.scrollY || 0);
      });
    };
    const el = getScroller();
    const target = el || window;
    target.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (raf) cancelAnimationFrame(raf);
      target.removeEventListener('scroll', onScroll);
    };
  }, [key, ready]);

  useEffect(() => {
    if (!ready || restoredRef.current) return;
    restoredRef.current = true;
    if (navType !== 'POP') return;
    let saved = 0;
    try {
      saved = Number(sessionStorage.getItem(key) || 0);
    } catch {
      saved = 0;
    }
    if (saved > 0) {
      requestAnimationFrame(() => {
        const el = getScroller();
        if (el) el.scrollTop = saved;
        else window.scrollTo(0, saved);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);
}
